import React, { useMemo, useState } from 'react';
import { AlertTriangle, BellRing, CheckCircle2, Loader2, Megaphone, OctagonX, Undo2 } from 'lucide-react';
import { pubblicaVerbale } from '../../services/verbaliService';
import { unpublishRepEventVerbale } from '../../services/representativeService';
import { controlloFinale } from '../../utils/verbaleSezioni';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';

interface VerbalePubblicaProps {
  eventId: string;
  bozza: boolean;
  pubblicatoAt: string | null;
  contenuto: string;
  sezioniRiviste: string[];
  allegati: unknown[];
  /** Numero di invitati (per il testo d'aiuto). */
  invitati?: number;
  /** Voci della trascrizione senza persona (piano 2): avviso nel controllo finale. */
  vociDaAssociare?: number;
  /** Foto dell'evento (piano 3, solo con il post social disponibile) e proposta del post: avviso. */
  foto?: number;
  socialRichiesto?: boolean;
  /** Partecipanti esterni dei dettagli: chi non compare nel testo ⇒ avviso. */
  partecipantiEsterni?: { nome: string; ruolo?: string }[];
  /** Salva le modifiche in sospeso prima di pubblicare: false ⇒ non si pubblica. */
  salvaSeServe: () => Promise<boolean>;
  /** Chiamata solo dopo una pubblicazione (o un nuovo avviso) riuscita, mai dopo un errore. */
  onPubblicato?: (pubblicatoAt: string) => void;
  onRitirato: () => void;
  /** Generazione o impaginazione in corso: pubblica, avvisa e ritira restano disabilitati. */
  bloccato?: boolean;
}

type Finestra = 'pubblica' | 'avvisa' | 'ritira' | null;

const dataLunga = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** Pubblicazione del verbale: controllo finale, conferma, nuovo avviso e ritiro. */
const VerbalePubblica: React.FC<VerbalePubblicaProps> = ({
  eventId,
  bozza,
  pubblicatoAt,
  contenuto,
  sezioniRiviste,
  allegati,
  invitati,
  vociDaAssociare = 0,
  foto = 0,
  socialRichiesto = false,
  partecipantiEsterni,
  salvaSeServe,
  onPubblicato,
  onRitirato,
  bloccato: generazioneInCorso = false,
}) => {
  const toast = useToast();
  const [finestra, setFinestra] = useState<Finestra>(null);
  const [inCorso, setInCorso] = useState(false);

  const esiti = useMemo(
    () => (finestra === 'pubblica'
      ? controlloFinale({ contenuto, sezioniRiviste, allegati, vociDaAssociare, foto, socialRichiesto, partecipantiEsterni })
      : []),
    [finestra, contenuto, sezioniRiviste, allegati, vociDaAssociare, foto, socialRichiesto, partecipantiEsterni]
  );
  const bloccato = esiti.some((e) => e.livello === 'blocco');

  const pubblica = async (giaPubblicato: boolean) => {
    if (generazioneInCorso) { setFinestra(null); return; }
    setInCorso(true);
    try {
      if (!(await salvaSeServe())) return;
      const res = await pubblicaVerbale(eventId, { notifica: true });
      onPubblicato?.(res.pubblicatoAt || new Date().toISOString());
      const avvisati = res.notified ? ` · ${res.notified} invitati avvisati` : '';
      toast.success(giaPubblicato ? `Invitati avvisati di nuovo${res.notified ? ` (${res.notified})` : ''}` : `Verbale pubblicato${avvisati}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || (giaPubblicato ? 'Avviso non inviato, riprova' : 'Pubblicazione non riuscita, riprova'));
    } finally {
      setInCorso(false);
    }
  };

  const ritira = async () => {
    if (generazioneInCorso) { setFinestra(null); return; }
    setInCorso(true);
    try {
      await unpublishRepEventVerbale(eventId);
      onRitirato();
      toast.success('Pubblicazione ritirata', 'Il verbale è tornato in bozza.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Ritiro non riuscito, riprova');
    } finally {
      setInCorso(false);
    }
  };

  const destinatari = invitati ? `ai ${invitati} invitati` : 'agli invitati';

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Pubblicazione">
      {bozza ? (
        <>
          <div className="mb-1 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Pubblicazione</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-gray-400">
            Prima della conferma ti mostro un controllo finale: sezioni vuote, da rivedere o con punti [da verificare].
          </p>
          <button
            type="button"
            onClick={() => setFinestra('pubblica')}
            disabled={inCorso || generazioneInCorso}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-emerald-900/20 transition-all hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50"
          >
            {inCorso ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Megaphone className="h-4 w-4" aria-hidden="true" />}
            Pubblica verbale agli invitati
          </button>
          <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">
            Email, push e campanella {destinatari}: dalla loro area riservata leggono il verbale e scaricano il PDF.
          </p>
        </>
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-xl bg-emerald-50 px-3 py-2.5 dark:bg-emerald-900/25">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Verbale pubblicato</p>
              <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80">
                {pubblicatoAt ? `Dal ${dataLunga(pubblicatoAt)}, ` : ''}visibile agli invitati. Le modifiche salvate le vedono subito.
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFinestra('avvisa')}
              disabled={inCorso || generazioneInCorso}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {inCorso ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <BellRing className="h-3.5 w-3.5" aria-hidden="true" />}
              Avvisa di nuovo gli invitati
            </button>
            <button
              type="button"
              onClick={() => setFinestra('ritira')}
              disabled={inCorso || generazioneInCorso}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
              Ritira pubblicazione
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={finestra === 'pubblica'}
        onClose={() => setFinestra(null)}
        onConfirm={() => { if (!bloccato) pubblica(false); }}
        title="Pubblicare il verbale?"
        message={bloccato
          ? 'Prima di pubblicare va sistemato questo:'
          : `Il verbale diventa visibile ${destinatari}, che ricevono email, push e campanella.`}
        confirmText="Pubblica e avvisa"
        confirmDisabled={bloccato}
        type="info"
      >
        {esiti.length > 0 ? (
          <ul className="mt-3 space-y-1.5" aria-label="Controllo finale">
            {esiti.map((e) => (
              <li
                key={e.testo}
                className={`flex items-start gap-2 rounded-lg px-2.5 py-2 text-xs ${
                  e.livello === 'blocco'
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                    : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
                }`}
              >
                {e.livello === 'blocco'
                  ? <OctagonX className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
                <span>{e.testo}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            Controllo finale superato: tutte le sezioni sono scritte e riviste.
          </p>
        )}
        {!bloccato && esiti.length > 0 && (
          <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">Sono avvisi: puoi pubblicare comunque.</p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={finestra === 'avvisa'}
        onClose={() => setFinestra(null)}
        onConfirm={() => pubblica(true)}
        title="Avvisare di nuovo gli invitati?"
        message={`Email, push e campanella ${destinatari} con il verbale aggiornato.`}
        confirmText="Avvisa"
        type="info"
      />

      <ConfirmDialog
        isOpen={finestra === 'ritira'}
        onClose={() => setFinestra(null)}
        onConfirm={ritira}
        title="Ritirare la pubblicazione?"
        message="Il verbale torna in bozza e gli invitati non lo vedono più finché non lo pubblichi di nuovo."
        confirmText="Ritira"
        type="warning"
      />
    </section>
  );
};

export default VerbalePubblica;
